import React, { useRef, useEffect, useState, useCallback } from 'react';
import { DocumentTextIcon, CogIcon, ChartBarIcon } from '@heroicons/react/outline';

// Hardcoded semantic color values (copied from previous attempt)
const colors = {
  light: {
    primary: '#133227',
    primaryForeground: '#ffffff',
    accent: '#BC8C04',
    accentForeground: '#ffffff',
    success: '#16a34a', // green-600
    successForeground: '#ffffff',
    mutedForeground: '#6b7280', // gray-500
    border: '#e5e7eb', // gray-200
    card: '#ffffff',
    cardForeground: '#030712', // gray-900
  },
  dark: {
    primary: '#0c211a',
    primaryForeground: '#ffcf40',
    accent: '#ffcf40',
    accentForeground: '#0c211a',
    success: '#15803d', // green-700
    successForeground: '#bbf7d0', // green-200
    mutedForeground: '#9ca3af', // gray-400 
    border: '#252525',
    card: '#1e1e1e', // dark-bg-secondary
    cardForeground: '#f3f4f6', // dark-text-primary
    darkAccent: '#ffcf40', // Need this explicitly for header
  },
};

interface DiagramNode {
  id: string;
  label: string;
  type: 'input' | 'process' | 'output';
  x: number;
  y: number;
}

interface DiagramEdge {
  from: string;
  to: string;
}

interface HowItWorksDiagramProps {
  nodes: DiagramNode[];
  edges: DiagramEdge[];
  className?: string;
}

// Map node types to semantic keys and icons
const nodeTypeMap = {
  input: { 
    bgKey: 'primary' as keyof typeof colors.light, 
    textKey: 'primaryForeground' as keyof typeof colors.light, 
    icon: DocumentTextIcon 
  },
  process: { 
    bgKey: 'accent' as keyof typeof colors.light, 
    textKey: 'accentForeground' as keyof typeof colors.light, 
    icon: CogIcon 
  },
  output: { 
    bgKey: 'success' as keyof typeof colors.light, 
    textKey: 'successForeground' as keyof typeof colors.light, 
    icon: ChartBarIcon 
  },
};

interface ViewBox {
  x: number;
  y: number;
  width: number;
  height: number;
}

const HowItWorksDiagram: React.FC<HowItWorksDiagramProps> = ({ 
  nodes, 
  edges,
  className = '',
}) => {
  const currentTheme = useRef<'light' | 'dark'>('light');
  const svgContainerRef = useRef<HTMLDivElement>(null); // Ref for the container div
  const svgRef = useRef<SVGSVGElement>(null); // Ref for the SVG element itself
  
  // --- State for Panning & Zooming ---
  const [isDragging, setIsDragging] = useState(false);
  const [startDragPos, setStartDragPos] = useState({ x: 0, y: 0 });
  const [startViewBox, setStartViewBox] = useState<ViewBox | null>(null);
  const [viewBoxParams, setViewBoxParams] = useState<ViewBox>({
    x: 0, 
    y: 0, 
    width: 600, // Initial default width
    height: 450 // Initial default height
  });
  // --- End State ---

  // Calculate initial viewBox based on content
  useEffect(() => {
    currentTheme.current = document.documentElement.classList.contains('dark') ? 'dark' : 'light';
    
    const scaleY = 1.8;
    const nodeRadius = 25;
    const labelOffset = nodeRadius + 15;
    const scaledNodes = nodes.map(node => ({ ...node, y: node.y * scaleY }));
    const padding = 50; // Padding around content

    if (scaledNodes.length > 0) {
      const minX = Math.min(...scaledNodes.map(n => n.x)) - nodeRadius - padding;
      const minY = Math.min(...scaledNodes.map(n => n.y)) - nodeRadius - padding;
      const maxX = Math.max(...scaledNodes.map(n => n.x)) + nodeRadius + padding;
      const maxY = Math.max(...scaledNodes.map(n => n.y)) + nodeRadius + labelOffset + padding;
      
      const contentWidth = maxX - minX;
      const contentHeight = maxY - minY;
      
      setViewBoxParams({ 
        x: minX, 
        y: minY, 
        width: contentWidth > 0 ? contentWidth : 600, // Use content width or default 
        height: contentHeight > 0 ? contentHeight : 450 // Use content height or default
      });
    } else {
      // Default if no nodes
      setViewBoxParams({ x: 0, y: 0, width: 600, height: 450 });
    }

  }, [nodes]); // Recalculate if nodes change

  const theme = currentTheme.current;
  const themeColors = colors[theme];
  const nodeRadius = 25;
  const iconSize = 20; 
  const labelOffset = nodeRadius + 15; 
  const scaleY = 1.8; 
  const scaledNodes = nodes.map(node => ({ ...node, y: node.y * scaleY }));

  // --- Panning Handlers (updates viewBox x/y) ---
  const handleMouseDown = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
    if (!svgContainerRef.current) return;
    setIsDragging(true);
    setStartDragPos({ x: e.clientX, y: e.clientY });
    setStartViewBox(viewBoxParams);
    svgContainerRef.current.style.cursor = 'grabbing';
    svgContainerRef.current.style.userSelect = 'none';
  }, [viewBoxParams]);

  const handleMouseMove = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
    if (!isDragging || !startViewBox || !svgRef.current) return;
    e.preventDefault();

    const svgRect = svgRef.current.getBoundingClientRect();
    // Calculate scale factor based on viewBox width and SVG element width
    const scale = viewBoxParams.width / svgRect.width;
    
    const dx = (e.clientX - startDragPos.x) * scale;
    const dy = (e.clientY - startDragPos.y) * scale;

    setViewBoxParams({
      ...startViewBox,
      x: startViewBox.x - dx,
      y: startViewBox.y - dy,
    });
  }, [isDragging, startDragPos, startViewBox, viewBoxParams.width]);

  const handleMouseUpOrLeave = useCallback(() => {
    if (!svgContainerRef.current) return;
    if (isDragging) {
        setIsDragging(false);
        setStartViewBox(null);
        svgContainerRef.current.style.cursor = 'grab';
        svgContainerRef.current.style.removeProperty('user-select');
    }
  }, [isDragging]);
  // --- End Panning Handlers ---

  // --- Zoom Handler (updates viewBox width/height and x/y) ---
  const handleWheel = useCallback((e: React.WheelEvent<HTMLDivElement>) => {
    if (!svgRef.current) return;
    e.preventDefault();

    const svgRect = svgRef.current.getBoundingClientRect();
    const zoomFactor = 1.1;
    const zoomSpeed = e.deltaY < 0 ? 1 / zoomFactor : zoomFactor;

    // Calculate mouse position within SVG element
    const mouseX = e.clientX - svgRect.left;
    const mouseY = e.clientY - svgRect.top;

    // Convert mouse position to SVG coordinates
    const pointX = viewBoxParams.x + mouseX * (viewBoxParams.width / svgRect.width);
    const pointY = viewBoxParams.y + mouseY * (viewBoxParams.height / svgRect.height);

    // Calculate new viewBox dimensions
    const newWidth = viewBoxParams.width * zoomSpeed;
    const newHeight = viewBoxParams.height * zoomSpeed;

    // Calculate new viewBox position to keep mouse point centered
    const newX = pointX - mouseX * (newWidth / svgRect.width);
    const newY = pointY - mouseY * (newHeight / svgRect.height);

    setViewBoxParams({ x: newX, y: newY, width: newWidth, height: newHeight });
  }, [viewBoxParams]);
  // --- End Zoom Handler ---

  // Define container height - should allow content to dictate size more freely now
  const containerHeightClass = "h-[450px]"; // Keep a fixed height for the container

  return (
    <div className={`relative bg-card dark:bg-dark-card rounded-lg shadow-soft dark:shadow-dark-card overflow-hidden ${className}`}>
      <div className="p-6 border-b border-border dark:border-dark-border">
        <h3 className="text-lg font-semibold text-card-foreground dark:text-dark-accent">How It Works</h3>
      </div>
      
      {/* Updated SVG container: overflow hidden, pan/zoom handlers */}
      <div 
        ref={svgContainerRef} // Ref on the container
        className={`relative ${containerHeightClass} flex justify-center items-center overflow-hidden cursor-grab`} 
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUpOrLeave}
        onMouseLeave={handleMouseUpOrLeave}
        onWheel={handleWheel} // Added wheel handler
      > 
        <svg 
          ref={svgRef} // Ref on the SVG element
          viewBox={`${viewBoxParams.x} ${viewBoxParams.y} ${viewBoxParams.width} ${viewBoxParams.height}`} 
          xmlns="http://www.w3.org/2000/svg"
          aria-labelledby="diagramTitle"
          // Prevent SVG elements from interfering with drag events when dragging
          style={{ pointerEvents: isDragging ? 'none' : 'auto' }} 
          className="max-w-full max-h-full" // Allow SVG to scale within container
        >
          <title id="diagramTitle">Solution Process Flowchart</title>
          <g fill="none" stroke={themeColors.border} strokeWidth="2" strokeDasharray="4 2">
            {edges.map((edge, index) => {
              const fromNode = scaledNodes.find(n => n.id === edge.from);
              const toNode = scaledNodes.find(n => n.id === edge.to);
              if (!fromNode || !toNode) return null;
              const angle = Math.atan2(toNode.y - fromNode.y, toNode.x - fromNode.x);
              const startX = fromNode.x + nodeRadius * Math.cos(angle);
              const startY = fromNode.y + nodeRadius * Math.sin(angle);
              const endX = toNode.x - nodeRadius * Math.cos(angle);
              const endY = toNode.y - nodeRadius * Math.sin(angle);
              const midX = startX + (endX - startX) / 2;
              const points = `${startX},${startY} ${midX},${startY} ${midX},${endY} ${endX},${endY}`;
              return <polyline key={`edge-${index}`} points={points} />;
            })}
          </g>
          {scaledNodes.map((node) => { 
             const typeInfo = nodeTypeMap[node.type];
             const nodeFillColor = themeColors[typeInfo.bgKey as keyof typeof themeColors]; // Added assertion
             const iconStrokeColor = themeColors[typeInfo.textKey as keyof typeof themeColors]; // Added assertion
             const IconComponent = typeInfo.icon;
            return (
              <g key={node.id} transform={`translate(${node.x}, ${node.y})`}> 
                <circle cx={0} cy={0} r={nodeRadius} fill={nodeFillColor} />
                <IconComponent x={-iconSize / 2} y={-iconSize / 2} width={iconSize} height={iconSize} stroke={iconStrokeColor} strokeWidth={1.5} />
                <text x={0} y={labelOffset} fontSize="12" textAnchor="middle" fill={themeColors.mutedForeground}>
                  {node.label}
                </text>
              </g>
            )}
          )}
        </svg>
      </div>
    </div>
  );
};

export default HowItWorksDiagram; 